// SCR-STAFF-104 — صف تکراری‌های احتمالی (AP-PAT-02، BR-PAT-041): جفت‌های مشابه یافته‌شده با Job شبانه یا اسکن دستی؛ تصمیم
// «ادغام» (ویزارد SCR-STAFF-105)، «فرد متفاوت» یا «بعداً». مجوز pat.duplicates.review.
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type DuplicateCandidateView, type DuplicateStatus } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, ListPage, toJalaliDateTime, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { mergePath, patientPath, patKeys } from './common';
import { PatientResultSummary } from './PatientSearchPalette';

export const SCR_STAFF_104 = 'SCR-STAFF-104';
const STATUSES: DuplicateStatus[] = ['Pending', 'Later', 'Distinct', 'Merged'];

export function DuplicatesPage() {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('pat.duplicates.review');
  const [status, setStatus] = useState<DuplicateStatus>('Pending');
  const candidates = useQuery({
    queryKey: patKeys.duplicateCandidates(session?.tenantId, status),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/duplicate-candidates', { params: { query: { status } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'duplicate-candidates'] });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: DuplicateStatus }) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/duplicate-candidates/{candidateId}:decide', {
          params: { path: { candidateId: id } },
          body: { decision },
        }),
      ),
    onSuccess: refresh,
  });
  const scan = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/patients/duplicate-candidates:scan')),
    onSuccess: refresh,
  });
  const person = (item: DuplicateCandidateView['first']) => (
    <Stack>
      <RouterLink to={patientPath(item.id)}>{item.displayName}</RouterLink>
      <small>
        <PatientResultSummary item={item} />
      </small>
    </Stack>
  );
  const columns: DataTableColumn<DuplicateCandidateView>[] = [
    { id: 'first', headerKey: 'pat:duplicates_queue.first', cell: (row) => person(row.first) },
    { id: 'second', headerKey: 'pat:duplicates_queue.second', cell: (row) => person(row.second) },
    {
      id: 'reasons',
      headerKey: 'pat:duplicates_queue.reasons',
      cell: (row) =>
        `${row.reasons.map((reason) => t(`pat:duplicates_queue.reason.${reason}`)).join(t('pat:list_separator'))} · ${t('pat:duplicates_queue.score', { score: faDigits(Math.round(Number(row.score))) })}`,
    },
    { id: 'detected', headerKey: 'pat:duplicates_queue.detected', cell: (row) => toJalaliDateTime(row.detectedAt) },
    {
      id: 'actions',
      headerKey: 'pat:duplicates_queue.decide',
      cell: (row) =>
        write.canWrite && (row.status === 'Pending' || row.status === 'Later') ? (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" component={RouterLink} to={mergePath(row.first.id, row.second.id)} data-testid="duplicate-merge">
              {t('pat:duplicates_queue.merge')}
            </Button>
            <Button size="small" onClick={() => decide.mutate({ id: row.id, decision: 'Distinct' })} data-testid="duplicate-distinct">
              {t('pat:duplicates_queue.distinct')}
            </Button>
            {row.status === 'Pending' && (
              <Button size="small" color="inherit" onClick={() => decide.mutate({ id: row.id, decision: 'Later' })}>
                {t('pat:duplicates_queue.later')}
              </Button>
            )}
          </Stack>
        ) : (
          t(`pat:duplicates_queue.status.${row.status}`)
        ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_104}
      titleKey="pat:duplicates_queue.title"
      subtitleKey="pat:duplicates_queue.subtitle"
      actions={
        write.canWrite ? (
          <Button variant="outlined" onClick={() => scan.mutate()} disabled={scan.isPending} data-testid="duplicates-scan">
            {t('pat:duplicates_queue.scan')}
          </Button>
        ) : undefined
      }
      filters={
        <TextField
          select
          size="small"
          label={t('pat:duplicates_queue.status_label')}
          value={status}
          onChange={(event) => setStatus(event.target.value as DuplicateStatus)}
          sx={{ minWidth: 200 }}
        >
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`pat:duplicates_queue.status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      {scan.data && <span data-testid="duplicates-scan-result">{t('pat:duplicates_queue.scan_result', { count: Number(scan.data.found) })}</span>}
      {decide.error ? <ErrorState error={decide.error} compact /> : null}
      <DataTable
        captionKey="pat:duplicates_queue.title"
        columns={columns}
        rows={candidates.data?.items}
        isLoading={candidates.isPending}
        error={candidates.error}
        onRetry={() => void candidates.refetch()}
        getRowId={(row) => row.id}
        emptyTitleKey="pat:duplicates_queue.empty"
      />
    </ListPage>
  );
}
